// Browser-compatible PDF report generation using jspdf
// Replaces server/pdf-report.ts which used pdfkit (Node.js only)

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { RepoStats, AuthorStats, Commit } from "./types";

// ==================== Shared Types ====================

export interface ReportData {
  repoName: string;
  repoType: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  stats: RepoStats;
  commits: Commit[];
}

export interface IndividualReportData {
  repoName: string;
  repoType: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  author: AuthorStats;
  commits: Commit[];
  performanceReview?: {
    target: {
      targets: {
        minCommits?: number;
        minActiveDays?: number;
        minAdditions?: number;
        minNetLines?: number;
      };
      period: { start: string; end: string };
      notes?: string;
    };
    metrics: {
      overallProgress: number;
      commitsProgress: number;
      activeDaysProgress: number;
      additionsProgress: number;
      netLinesProgress: number;
    };
    status: string;
  };
}

export interface CrossProjectReportData {
  generatedAt: string;
  dateRange: { start: string; end: string };
  repos: {
    repoId: string;
    repoName: string;
    repoType: string;
    stats: {
      totalCommits: number;
      totalAuthors: number;
      totalAdditions: number;
      totalDeletions: number;
      authors: { name: string; commits: number; additions: number; deletions: number; activeDays: number }[];
    } | null;
    error?: string;
  }[];
  aggregateStats: {
    totalCommits: number;
    totalAuthors: number;
    totalAdditions: number;
    totalDeletions: number;
    totalRepos: number;
  };
  commits: {
    hash: string;
    author: string;
    email: string;
    date: string;
    message: string;
    branch: string;
    additions: number;
    deletions: number;
    isMerge?: boolean;
    repoName: string;
    repoId: string;
  }[];
}

export interface CombinedReportData {
  generatedAt: string;
  dateRange: { start: string; end: string };
  lookbackDays: number;
  jiraProjectKey?: string;
  repos: { repoId: string; repoName: string; repoType: string }[];
  gitSummary: {
    totalCommits: number;
    totalAdditions: number;
    totalDeletions: number;
    repoBreakdown: { repo: string; commits: number }[];
  };
  jiraSummary: {
    totalEvents: number;
    statusBreakdown: { status: string; count: number }[];
    issueTypeBreakdown: { type: string; count: number }[];
    sprintSummary: { name: string; state: string; totalIssues: number; completedIssues: number; startDate?: string; endDate?: string }[];
  };
  userProfiles: {
    name: string;
    gitCommits: number;
    jiraEvents: number;
    additions: number;
    deletions: number;
  }[];
  linkedItems: {
    issueKey: string;
    issueSummary?: string;
    issueType?: string;
    latestStatus?: string;
    commitCount: number;
    jiraEventCount: number;
  }[];
  recentTimeline: {
    timestamp: string;
    source: "git" | "jira";
    userName: string;
    summary: string;
  }[];
}

export interface JiraReportData {
  generatedAt: string;
  dateRange: { start: string; end: string };
  lookbackDays: number;
  projectKey: string;
  projectName?: string;
  summary: {
    totalEvents: number;
    totalDevelopers: number;
    statusBreakdown: { status: string; count: number }[];
    issueTypeBreakdown: { type: string; count: number }[];
    sprintSummary: { name: string; state: string; totalIssues: number; completedIssues: number; startDate?: string; endDate?: string }[];
    developers: { displayName: string; issueCount: number }[];
  };
  lagIndicators: {
    name: string;
    overallLabel: string;
    severity: "good" | "warning" | "critical";
    results: { developerName: string; label: string; severity: "good" | "warning" | "critical" }[];
  }[];
  leadIndicators: {
    name: string;
    overallLabel: string;
    severity: "good" | "warning" | "critical";
    results: { developerName: string; label: string; severity: "good" | "warning" | "critical" }[];
  }[];
  aggregateIndicators: {
    name: string;
    label: string;
    severity: "good" | "warning" | "critical";
    baseline: string;
  }[];
  recentEvents: {
    timestamp: string;
    type: string;
    issueKey: string;
    issueSummary: string;
    issueType: string;
    author: string;
    detail?: string;
  }[];
}

// ==================== Helpers ====================

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

const COLORS = {
  black: [0, 0, 0] as [number, number, number],
  gray: [102, 102, 102] as [number, number, number],
  lightGray: [153, 153, 153] as [number, number, number],
  blue: [37, 99, 235] as [number, number, number],
  green: [5, 150, 105] as [number, number, number],
  amber: [217, 119, 6] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  disclaimerTitle: [180, 83, 9] as [number, number, number],
  disclaimerText: [146, 64, 14] as [number, number, number],
};

function severityColor(s: string): [number, number, number] {
  return s === "good" ? COLORS.green : s === "warning" ? COLORS.amber : COLORS.red;
}

function severitySymbol(s: string): string {
  return s === "good" ? "+" : s === "warning" ? "!" : "X";
}

function addDisclaimer(doc: jsPDF, y: number): number {
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.disclaimerTitle);
  doc.text("Important Disclaimer", 20, y);
  y += 5;
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.disclaimerText);
  const disclaimer =
    "Lines of code (additions/deletions) are not a reliable indicator of developer productivity or output quality. " +
    "Meaningful assessment should consider time spent, code quality, problem complexity, collaboration, and value delivered. " +
    "This report is for informational purposes only.";
  const lines = doc.splitTextToSize(disclaimer, 170);
  doc.text(lines, 20, y);
  return y + lines.length * 3.5 + 5;
}

function checkPageBreak(doc: jsPDF, y: number, _needed: number = 20): number {
  if (y > 270) {
    doc.addPage();
    return 20;
  }
  return y;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function colorAddDel(addCols: number[], delCols: number[], netCols: number[] = []): (data: any) => void {
  return (data: any) => {
    if (data.section !== "body") return;
    if (addCols.includes(data.column.index)) {
      data.cell.styles.textColor = [5, 150, 105]; // green
    }
    if (delCols.includes(data.column.index)) {
      data.cell.styles.textColor = [220, 38, 38]; // red
    }
    if (netCols.includes(data.column.index)) {
      const raw = String(data.cell.raw);
      if (raw.startsWith("-")) {
        data.cell.styles.textColor = [220, 38, 38]; // red
      } else {
        data.cell.styles.textColor = [5, 150, 105]; // green
      }
    }
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function colorSprintTable(pctCol: number, forecastCol: number): (data: any) => void {
  return (data: any) => {
    if (data.section !== "body") return;
    if (data.column.index === pctCol) {
      const val = parseInt(String(data.cell.raw));
      if (!isNaN(val)) {
        data.cell.styles.textColor = val >= 90 ? [5, 150, 105] : val >= 70 ? [217, 119, 6] : [220, 38, 38];
        data.cell.styles.fontStyle = "bold";
      }
    }
    if (data.column.index === forecastCol) {
      const raw = String(data.cell.raw);
      if (raw.includes("On Track") || raw.includes("Met")) {
        data.cell.styles.textColor = [5, 150, 105];
        data.cell.styles.fontStyle = "bold";
      } else if (raw.includes("At Risk")) {
        data.cell.styles.textColor = [217, 119, 6];
        data.cell.styles.fontStyle = "bold";
      } else if (raw.includes("Behind") || raw.includes("Missed")) {
        data.cell.styles.textColor = [220, 38, 38];
        data.cell.styles.fontStyle = "bold";
      }
    }
  };
}

function sprintForecast(
  sprints: { name: string; state: string; totalIssues: number; completedIssues: number; startDate?: string; endDate?: string }[],
): string[] {
  // Calculate average completion rate from closed sprints
  const closed = sprints.filter(s => s.state === "closed" && s.totalIssues > 0);
  const avgClosedPct = closed.length > 0
    ? closed.reduce((sum, s) => sum + (s.completedIssues / s.totalIssues) * 100, 0) / closed.length
    : 90; // default assumption

  return sprints.map((sprint) => {
    const pct = sprint.totalIssues > 0 ? (sprint.completedIssues / sprint.totalIssues) * 100 : 0;

    if (sprint.state === "closed") {
      return pct >= 90 ? "Met" : "Missed";
    }

    if (sprint.state === "active" && sprint.startDate && sprint.endDate) {
      const start = new Date(sprint.startDate).getTime();
      const end = new Date(sprint.endDate).getTime();
      const now = Date.now();
      const totalDuration = end - start;
      const elapsed = now - start;
      const timeProgress = totalDuration > 0 ? Math.min((elapsed / totalDuration) * 100, 100) : 50;

      // Expected completion at this point based on historical avg
      const expectedPct = (timeProgress / 100) * avgClosedPct;
      const ratio = expectedPct > 0 ? pct / expectedPct : 1;

      if (ratio >= 0.85) return "On Track";
      if (ratio >= 0.6) return "At Risk";
      return "Behind";
    }

    // Future sprints
    return "-";
  });
}

// ==================== Main Report ====================

export function generatePDFReport(data: ReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("VeriGit Report", 105, y, { align: "center" });
  y += 8;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(data.repoName, 105, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} - ${formatDate(data.dateRange.end)}`, 105, y, { align: "center" });
  y += 8;

  y = addDisclaimer(doc, y);
  y += 5;

  // Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Summary", 20, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const summaryItems = [
    ["Total Commits", data.stats.totalCommits.toString()],
    ["Contributors", data.stats.totalAuthors.toString()],
    ["Branches", data.stats.totalBranches.toString()],
    ["Lines Added", `+${data.stats.totalAdditions.toLocaleString()}`],
    ["Lines Deleted", `-${data.stats.totalDeletions.toLocaleString()}`],
    ["Net Change", `${(data.stats.totalAdditions - data.stats.totalDeletions) >= 0 ? '+' : ''}${(data.stats.totalAdditions - data.stats.totalDeletions).toLocaleString()}`],
  ];
  for (const [label, value] of summaryItems) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.black);
    doc.text(`${label}: `, 20, y);
    doc.setFont("helvetica", "bold");
    if (label === "Lines Added") doc.setTextColor(...COLORS.green);
    else if (label === "Lines Deleted") doc.setTextColor(...COLORS.red);
    else if (label === "Net Change") doc.setTextColor(...(value.startsWith("-") ? COLORS.red : COLORS.green));
    else doc.setTextColor(...COLORS.black);
    doc.text(value, 60, y);
    y += 5;
  }
  doc.setTextColor(...COLORS.black);
  y += 5;

  // Contributors table
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Contributors", 20, y);
  y += 3;

  const sortedAuthors = [...data.stats.authors].sort((a, b) => b.commits - a.commits);
  autoTable(doc, {
    startY: y,
    head: [["Author", "Commits", "Additions", "Deletions", "Active Days"]],
    body: sortedAuthors.slice(0, 15).map((a) => [
      a.name.substring(0, 25),
      a.commits.toString(),
      `+${a.additions.toLocaleString()}`,
      `-${a.deletions.toLocaleString()}`,
      a.activeDays.toString(),
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
    didParseCell: colorAddDel([2], [3]),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;

  // Branches
  if (data.stats.branches && data.stats.branches.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Branches", 20, y);
    y += 3;

    const branchRows = [...data.stats.branches]
      .sort((a, b) => b.commitCount - a.commitCount)
      .slice(0, 25)
      .map((b) => [
        (b.isRemote ? "(remote) " : "") + b.name.substring(0, 25),
        b.commitCount.toString(),
        b.lastAuthor ? b.lastAuthor.substring(0, 18) : "-",
        b.lastCommit ? formatDate(b.lastCommit) : "-",
      ]);
    autoTable(doc, {
      startY: y,
      head: [["Branch", "Commits", "Last Author", "Last Commit"]],
      body: branchRows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Daily Activity
  if (data.stats.dailyActivity && data.stats.dailyActivity.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Daily Activity", 20, y);
    y += 3;

    const dailyRows = [...data.stats.dailyActivity]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 30)
      .map((d) => [
        formatDate(d.date),
        d.commits.toString(),
        d.authors.length.toString(),
        `+${d.additions.toLocaleString()}`,
        `-${d.deletions.toLocaleString()}`,
      ]);
    autoTable(doc, {
      startY: y,
      head: [["Date", "Commits", "Authors", "Additions", "Deletions"]],
      body: dailyRows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
      didParseCell: colorAddDel([3], [4]),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Recent Commits
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Recent Commits", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Hash", "Message", "Author", "Branch", "Date", "+/-"]],
    body: data.commits.slice(0, 50).map((c) => [
      c.hash.substring(0, 7),
      c.message.substring(0, 40) + (c.message.length > 40 ? "..." : ""),
      c.author.substring(0, 14),
      (c.branch || "").substring(0, 14),
      formatDate(c.date),
      `+${c.additions}/-${c.deletions}`,
    ]),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
  });

  // Footer
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;
  y = checkPageBreak(doc, y);
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  doc.text(`Report generated by VeriGit | ${data.repoType} repository`, 105, y, { align: "center" });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Individual Report ====================

export function generateIndividualPDFReport(data: IndividualReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("Individual Performance Report", 105, y, { align: "center" });
  y += 8;
  doc.setFontSize(16);
  doc.setTextColor(51, 51, 51);
  doc.text(data.author.name, 105, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  if (data.author.email) doc.text(data.author.email, 105, y, { align: "center" });
  y += 4;
  doc.text(`Repository: ${data.repoName}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} - ${formatDate(data.dateRange.end)}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 8;

  y = addDisclaimer(doc, y);
  y += 5;

  // Performance Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Performance Summary", 20, y);
  y += 7;

  const { author } = data;
  const summaryItems = [
    ["Total Commits", author.commits.toString()],
    ["Active Days", author.activeDays.toString()],
    ["Avg Commits/Day", author.avgCommitsPerDay.toFixed(1)],
    ["Lines Added", `+${author.additions.toLocaleString()}`],
    ["Lines Deleted", `-${author.deletions.toLocaleString()}`],
    ["Net Lines", `${(author.additions - author.deletions) >= 0 ? '+' : ''}${(author.additions - author.deletions).toLocaleString()}`],
    ["First Commit", formatDate(author.firstCommit)],
    ["Last Commit", formatDate(author.lastCommit)],
  ];
  doc.setFontSize(10);
  for (const [label, value] of summaryItems) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.black);
    doc.text(`${label}: `, 20, y);
    doc.setFont("helvetica", "bold");
    if (label === "Lines Added") doc.setTextColor(...COLORS.green);
    else if (label === "Lines Deleted") doc.setTextColor(...COLORS.red);
    else if (label === "Net Lines") doc.setTextColor(...(value.startsWith("-") ? COLORS.red : COLORS.green));
    else doc.setTextColor(...COLORS.black);
    doc.text(value, 65, y);
    y += 5;
  }
  doc.setTextColor(...COLORS.black);
  y += 5;

  // Performance Targets
  if (data.performanceReview) {
    y = checkPageBreak(doc, y);
    const { target, metrics, status } = data.performanceReview;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Performance Targets", 20, y);
    y += 7;

    const statusColorMap: Record<string, [number, number, number]> = {
      exceeded: COLORS.green,
      on_track: COLORS.blue,
      at_risk: COLORS.amber,
      behind: COLORS.red,
    };
    doc.setFontSize(11);
    doc.setTextColor(...(statusColorMap[status] || COLORS.gray));
    doc.text(`Status: ${status.toUpperCase()} (${Math.round(metrics.overallProgress)}%)`, 20, y);
    y += 6;

    doc.setFontSize(9);
    if (target.targets.minCommits) {
      const met = author.commits >= target.targets.minCommits;
      doc.setTextColor(...(met ? COLORS.green : COLORS.red));
      doc.text(`${met ? "+" : "X"} Commits: ${author.commits} / ${target.targets.minCommits} (${Math.round(metrics.commitsProgress)}%)`, 20, y);
      y += 4;
    }
    if (target.targets.minActiveDays) {
      const met = author.activeDays >= target.targets.minActiveDays;
      doc.setTextColor(...(met ? COLORS.green : COLORS.red));
      doc.text(`${met ? "+" : "X"} Active Days: ${author.activeDays} / ${target.targets.minActiveDays} (${Math.round(metrics.activeDaysProgress)}%)`, 20, y);
      y += 4;
    }
    if (target.targets.minAdditions) {
      const met = author.additions >= target.targets.minAdditions;
      doc.setTextColor(...(met ? COLORS.green : COLORS.red));
      doc.text(`${met ? "+" : "X"} Lines Added: ${author.additions.toLocaleString()} / ${target.targets.minAdditions.toLocaleString()} (${Math.round(metrics.additionsProgress)}%)`, 20, y);
      y += 4;
    }
    if (target.targets.minNetLines) {
      const netLines = author.additions - author.deletions;
      const met = netLines >= target.targets.minNetLines;
      doc.setTextColor(...(met ? COLORS.green : COLORS.red));
      doc.text(`${met ? "+" : "X"} Net Lines: ${netLines.toLocaleString()} / ${target.targets.minNetLines.toLocaleString()} (${Math.round(metrics.netLinesProgress)}%)`, 20, y);
      y += 4;
    }

    if (target.notes) {
      y += 2;
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.gray);
      doc.text(`Notes: ${target.notes}`, 20, y);
      y += 4;
    }
    y += 5;
  }

  // Recent Commits
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Recent Commits", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Hash", "Message", "Date", "+/-"]],
    body: data.commits.slice(0, 25).map((c) => [
      c.hash.substring(0, 7),
      c.message.substring(0, 55) + (c.message.length > 55 ? "..." : ""),
      formatDate(c.date),
      `+${c.additions}/-${c.deletions}`,
    ]),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  doc.text(`Individual Report generated by VeriGit | ${data.repoType} repository`, 105, y, { align: "center" });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Cross-Project Report ====================

export function generateCrossProjectPDFReport(data: CrossProjectReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("VeriGit Cross-Project Report", 105, y, { align: "center" });
  y += 7;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  doc.text(`${data.repos.length} Repositories Analyzed`, 105, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} - ${formatDate(data.dateRange.end)}`, 105, y, { align: "center" });
  y += 8;

  y = addDisclaimer(doc, y);
  y += 5;

  // Aggregate Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Aggregate Summary", 20, y);
  y += 7;

  const agg = data.aggregateStats;
  doc.setFontSize(10);
  const aggItems = [
    ["Repositories", agg.totalRepos.toString()],
    ["Total Commits", agg.totalCommits.toLocaleString()],
    ["Total Contributors", agg.totalAuthors.toString()],
    ["Lines Added", `+${agg.totalAdditions.toLocaleString()}`],
    ["Lines Deleted", `-${agg.totalDeletions.toLocaleString()}`],
    ["Net Change", `${(agg.totalAdditions - agg.totalDeletions) >= 0 ? '+' : ''}${(agg.totalAdditions - agg.totalDeletions).toLocaleString()}`],
  ];
  for (const [label, value] of aggItems) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.black);
    doc.text(`${label}: `, 20, y);
    doc.setFont("helvetica", "bold");
    if (label === "Lines Added") doc.setTextColor(...COLORS.green);
    else if (label === "Lines Deleted") doc.setTextColor(...COLORS.red);
    else if (label === "Net Change") doc.setTextColor(...(value.startsWith("-") ? COLORS.red : COLORS.green));
    else doc.setTextColor(...COLORS.black);
    doc.text(value, 65, y);
    y += 5;
  }
  doc.setTextColor(...COLORS.black);
  y += 5;

  // Repository Breakdown table
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Repository Breakdown", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Repository", "Commits", "Authors", "Additions", "Deletions"]],
    body: data.repos.map((r) =>
      r.error
        ? [r.repoName.substring(0, 22), "Error", "-", "-", "-"]
        : [
            r.repoName.substring(0, 22),
            (r.stats?.totalCommits || 0).toString(),
            (r.stats?.totalAuthors || 0).toString(),
            `+${(r.stats?.totalAdditions || 0).toLocaleString()}`,
            `-${(r.stats?.totalDeletions || 0).toLocaleString()}`,
          ]
    ),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
    didParseCell: colorAddDel([3], [4]),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;

  // Cross-Project Contributors
  y = checkPageBreak(doc, y);
  const authorMap = new Map<string, { commits: number; additions: number; deletions: number; repos: Set<string> }>();
  for (const commit of data.commits) {
    if (!authorMap.has(commit.author)) {
      authorMap.set(commit.author, { commits: 0, additions: 0, deletions: 0, repos: new Set() });
    }
    const a = authorMap.get(commit.author)!;
    a.commits++;
    a.additions += commit.additions;
    a.deletions += commit.deletions;
    a.repos.add(commit.repoName);
  }
  const crossAuthors = Array.from(authorMap.entries())
    .map(([name, s]) => ({ name, ...s, repoCount: s.repos.size }))
    .sort((a, b) => b.commits - a.commits);

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Cross-Project Contributors", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Contributor", "Commits", "Repos", "Additions", "Deletions"]],
    body: crossAuthors.slice(0, 20).map((a) => [
      a.name.substring(0, 20),
      a.commits.toString(),
      a.repoCount.toString(),
      `+${a.additions.toLocaleString()}`,
      `-${a.deletions.toLocaleString()}`,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
    didParseCell: colorAddDel([3], [4]),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;

  // Per-Repo Author Breakdown
  for (const repo of data.repos) {
    if (repo.error || !repo.stats || !repo.stats.authors || repo.stats.authors.length === 0) continue;
    y = checkPageBreak(doc, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.blue);
    doc.text(`Contributors — ${repo.repoName}`, 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Author", "Commits", "Additions", "Deletions", "Active Days"]],
      body: [...repo.stats.authors]
        .sort((a, b) => b.commits - a.commits)
        .slice(0, 10)
        .map((a) => [
          a.name.substring(0, 22),
          a.commits.toString(),
          `+${a.additions.toLocaleString()}`,
          `-${a.deletions.toLocaleString()}`,
          a.activeDays.toString(),
        ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 65, 85] },
      margin: { left: 20, right: 20 },
      didParseCell: colorAddDel([2], [3]),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Daily Activity (aggregated across repos)
  {
    const dayMap = new Map<string, { commits: number; additions: number; deletions: number; authors: Set<string> }>();
    for (const c of data.commits) {
      const day = c.date.substring(0, 10);
      if (!dayMap.has(day)) dayMap.set(day, { commits: 0, additions: 0, deletions: 0, authors: new Set() });
      const d = dayMap.get(day)!;
      d.commits++;
      d.additions += c.additions;
      d.deletions += c.deletions;
      d.authors.add(c.author);
    }
    const dailyRows = Array.from(dayMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 30)
      .map(([date, d]) => [
        formatDate(date),
        d.commits.toString(),
        d.authors.size.toString(),
        `+${d.additions.toLocaleString()}`,
        `-${d.deletions.toLocaleString()}`,
      ]);
    if (dailyRows.length > 0) {
      y = checkPageBreak(doc, y);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.black);
      doc.text("Daily Activity (All Repos)", 20, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [["Date", "Commits", "Authors", "Additions", "Deletions"]],
        body: dailyRows,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [51, 51, 51] },
        margin: { left: 20, right: 20 },
        didParseCell: colorAddDel([3], [4]),
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (doc as any).lastAutoTable.finalY + 10;
    }
  }

  // Recent Commits (All Repos)
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Recent Commits (All Repos)", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Repo", "Hash", "Message", "Author", "Branch", "Date", "+/-"]],
    body: data.commits.slice(0, 100).map((c) => [
      c.repoName.substring(0, 10),
      c.hash.substring(0, 7),
      c.message.substring(0, 32) + (c.message.length > 32 ? "..." : ""),
      c.author.substring(0, 10),
      (c.branch || "").substring(0, 10),
      formatDate(c.date),
      `+${c.additions}/-${c.deletions}`,
    ]),
    styles: { fontSize: 6 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  doc.text(`Cross-Project Report generated by VeriGit | ${data.repos.length} repositories`, 105, y, { align: "center" });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Combined Report (Git + Jira) ====================

export function generateCombinedPDFReport(data: CombinedReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("VeriGit Combined Report", 105, y, { align: "center" });
  y += 7;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  doc.text("Git + Jira — Unified Analysis", 105, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} - ${formatDate(data.dateRange.end)} (${data.lookbackDays} days)`, 105, y, { align: "center" });
  if (data.jiraProjectKey) {
    y += 4;
    doc.text(`Jira Project: ${data.jiraProjectKey}`, 105, y, { align: "center" });
  }
  y += 4;
  doc.text(`${data.repos.length} repositor${data.repos.length !== 1 ? "ies" : "y"} analyzed`, 105, y, { align: "center" });
  y += 8;

  y = addDisclaimer(doc, y);
  y += 5;

  // Overview
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Overview", 20, y);
  y += 7;

  doc.setFontSize(10);
  const overviewItems = [
    ["Git Commits", data.gitSummary.totalCommits.toLocaleString()],
    ["Lines Added", `+${data.gitSummary.totalAdditions.toLocaleString()}`],
    ["Lines Deleted", `-${data.gitSummary.totalDeletions.toLocaleString()}`],
    ["Jira Events", data.jiraSummary.totalEvents.toLocaleString()],
    ["Linked Issues", data.linkedItems.length.toString()],
    ["Team Members", data.userProfiles.length.toString()],
  ];
  for (const [label, value] of overviewItems) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.black);
    doc.text(`${label}: `, 20, y);
    doc.setFont("helvetica", "bold");
    if (label === "Lines Added") doc.setTextColor(...COLORS.green);
    else if (label === "Lines Deleted") doc.setTextColor(...COLORS.red);
    else doc.setTextColor(...COLORS.black);
    doc.text(value, 60, y);
    y += 5;
  }
  doc.setTextColor(...COLORS.black);
  y += 5;

  // Team Activity table
  y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Team Activity", 20, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Name", "Commits", "Jira", "Additions", "Deletions", "Net"]],
    body: data.userProfiles.slice(0, 20).map((u) => [
      u.name.substring(0, 20),
      u.gitCommits.toString(),
      u.jiraEvents.toString(),
      `+${u.additions.toLocaleString()}`,
      `-${u.deletions.toLocaleString()}`,
      `${(u.additions - u.deletions) >= 0 ? '+' : ''}${(u.additions - u.deletions).toLocaleString()}`,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [51, 51, 51] },
    margin: { left: 20, right: 20 },
    didParseCell: colorAddDel([3], [4], [5]),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 10;

  // Sprint Summary
  if (data.jiraSummary.sprintSummary.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Sprint Summary", 20, y);
    y += 3;

    const forecasts = sprintForecast(data.jiraSummary.sprintSummary);
    autoTable(doc, {
      startY: y,
      head: [["Sprint", "Start", "End", "Done", "Total", "%", "Forecast"]],
      body: data.jiraSummary.sprintSummary.map((sprint, i) => {
        const pct = sprint.totalIssues > 0 ? Math.round((sprint.completedIssues / sprint.totalIssues) * 100) : 0;
        return [
          sprint.name.substring(0, 22),
          sprint.startDate ? formatDate(sprint.startDate) : "-",
          sprint.endDate ? formatDate(sprint.endDate) : "-",
          sprint.completedIssues.toString(),
          sprint.totalIssues.toString(),
          pct.toString(),
          forecasts[i],
        ];
      }),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 65, 85] },
      margin: { left: 20, right: 20 },
      didParseCell: colorSprintTable(5, 6),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Repo Breakdown table
  if (data.gitSummary.repoBreakdown && data.gitSummary.repoBreakdown.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Repository Breakdown", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Repository", "Commits"]],
      body: [...data.gitSummary.repoBreakdown]
        .sort((a, b) => b.commits - a.commits)
        .map((r) => [r.repo.substring(0, 30), r.commits.toString()]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Jira Status Breakdown
  if (data.jiraSummary.statusBreakdown && data.jiraSummary.statusBreakdown.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Jira Status Breakdown", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Status", "Count"]],
      body: [...data.jiraSummary.statusBreakdown]
        .sort((a, b) => b.count - a.count)
        .map((s) => [s.status, s.count.toString()]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [51, 65, 85] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Jira Issue Type Breakdown
  if (data.jiraSummary.issueTypeBreakdown && data.jiraSummary.issueTypeBreakdown.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Issue Type Breakdown", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Issue Type", "Count"]],
      body: [...data.jiraSummary.issueTypeBreakdown]
        .sort((a, b) => b.count - a.count)
        .map((t) => [t.type, t.count.toString()]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [51, 65, 85] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Linked Issues (Git ↔ Jira)
  if (data.linkedItems && data.linkedItems.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Linked Issues (Git + Jira)", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Issue", "Summary", "Type", "Status", "Commits", "Jira Events"]],
      body: data.linkedItems.slice(0, 30).map((li) => [
        li.issueKey,
        (li.issueSummary || "").substring(0, 30) + ((li.issueSummary || "").length > 30 ? "..." : ""),
        li.issueType || "-",
        li.latestStatus || "-",
        li.commitCount.toString(),
        li.jiraEventCount.toString(),
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Recent Timeline
  if (data.recentTimeline && data.recentTimeline.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Recent Activity Timeline", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Time", "Source", "User", "Activity"]],
      body: data.recentTimeline.slice(0, 50).map((t) => [
        formatDate(t.timestamp),
        t.source.toUpperCase(),
        t.userName.substring(0, 15),
        t.summary.substring(0, 50) + (t.summary.length > 50 ? "..." : ""),
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Footer
  y = checkPageBreak(doc, y);
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  doc.text(`Combined Report generated by VeriGit | ${data.repos.length} repos + Jira`, 105, y, { align: "center" });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Jira Report ====================

export function generateJiraPDFReport(data: JiraReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // Header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("VeriGit Jira Report", 105, y, { align: "center" });
  y += 7;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  doc.text(data.projectName ? `${data.projectName} (${data.projectKey})` : data.projectKey, 105, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} - ${formatDate(data.dateRange.end)} (${data.lookbackDays} days)`, 105, y, { align: "center" });
  y += 8;

  // Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text("Summary", 20, y);
  y += 7;

  doc.setFontSize(10);
  const jiraSummaryItems = [
    ["Total Events", data.summary.totalEvents.toLocaleString()],
    ["Developers", data.summary.totalDevelopers.toString()],
    ["Issue Types", data.summary.issueTypeBreakdown.length.toString()],
    ["Sprints", data.summary.sprintSummary.length.toString()],
  ];
  for (const [label, value] of jiraSummaryItems) {
    doc.setFont("helvetica", "normal");
    doc.text(`${label}: `, 20, y);
    doc.setFont("helvetica", "bold");
    doc.text(value, 60, y);
    y += 5;
  }
  y += 5;

  // Developer Activity
  if (data.summary.developers.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Developer Activity", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Developer", "Issues Touched"]],
      body: data.summary.developers.slice(0, 20).map((d) => [
        d.displayName.substring(0, 30),
        d.issueCount.toString(),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Sprint Summary
  if (data.summary.sprintSummary.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Sprint Summary", 20, y);
    y += 3;

    const forecasts = sprintForecast(data.summary.sprintSummary);
    autoTable(doc, {
      startY: y,
      head: [["Sprint", "Start", "End", "Done", "Total", "%", "Forecast"]],
      body: data.summary.sprintSummary.map((sprint, i) => {
        const pct = sprint.totalIssues > 0 ? Math.round((sprint.completedIssues / sprint.totalIssues) * 100) : 0;
        return [
          sprint.name.substring(0, 22),
          sprint.startDate ? formatDate(sprint.startDate) : "-",
          sprint.endDate ? formatDate(sprint.endDate) : "-",
          sprint.completedIssues.toString(),
          sprint.totalIssues.toString(),
          pct.toString(),
          forecasts[i],
        ];
      }),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 65, 85] },
      margin: { left: 20, right: 20 },
      didParseCell: colorSprintTable(5, 6),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Aggregate Health Indicators
  if (data.aggregateIndicators.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Project Health Indicators", 20, y);
    y += 6;

    for (const ind of data.aggregateIndicators) {
      y = checkPageBreak(doc, y);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...severityColor(ind.severity));
      doc.text(`${severitySymbol(ind.severity)} ${ind.name}: ${ind.label}`, 20, y);
      y += 4;
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.gray);
      doc.text(`  Baseline: ${ind.baseline}`, 20, y);
      y += 4;
    }
    y += 3;
  }

  // Lag Indicators
  if (data.lagIndicators.length > 0) {
    y = checkPageBreak(doc, y, 40);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Per-User Lag Indicators", 20, y);
    y += 6;

    for (const ind of data.lagIndicators) {
      y = checkPageBreak(doc, y, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...severityColor(ind.severity));
      doc.text(`${severitySymbol(ind.severity)} ${ind.name} - ${ind.overallLabel}`, 20, y);
      y += 5;

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      for (const r of ind.results.slice(0, 10)) {
        doc.setTextColor(...severityColor(r.severity));
        doc.text(`  ${severitySymbol(r.severity)} ${r.developerName}: ${r.label}`, 25, y);
        y += 3.5;
      }
      y += 2;
    }
    y += 3;
  }

  // Lead Indicators
  if (data.leadIndicators.length > 0) {
    y = checkPageBreak(doc, y, 40);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Per-User Lead Indicators", 20, y);
    y += 6;

    for (const ind of data.leadIndicators) {
      y = checkPageBreak(doc, y, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...severityColor(ind.severity));
      doc.text(`${severitySymbol(ind.severity)} ${ind.name} - ${ind.overallLabel}`, 20, y);
      y += 5;

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      for (const r of ind.results.slice(0, 10)) {
        doc.setTextColor(...severityColor(r.severity));
        doc.text(`  ${severitySymbol(r.severity)} ${r.developerName}: ${r.label}`, 25, y);
        y += 3.5;
      }
      y += 2;
    }
    y += 3;
  }

  // Recent Events
  if (data.recentEvents.length > 0) {
    y = checkPageBreak(doc, y);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Recent Activity", 20, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Issue", "Summary", "Type", "Author", "Date"]],
      body: data.recentEvents.slice(0, 40).map((e) => [
        e.issueKey,
        e.issueSummary.substring(0, 35) + (e.issueSummary.length > 35 ? "..." : ""),
        e.type.replace(/_/g, " "),
        e.author.substring(0, 15),
        formatDate(e.timestamp),
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 51, 51] },
      margin: { left: 20, right: 20 },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Footer
  y = checkPageBreak(doc, y);
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  doc.text(`Jira Report generated by VeriGit | Project: ${data.projectKey}`, 105, y, { align: "center" });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Confluence Report ====================

export interface ConfluenceReportData {
  generatedAt: string;
  dateRange: { start: string; end: string };
  spaceName: string;
  spaceKey: string;
  cloudName?: string;
  totalPages: number;
  uniqueContributors: number;
  pagesPerDay: number;
  newPagesRatio: number;
  activeDaysPct: number;
  authors: { name: string; count: number }[];
  dailyCounts: { date: string; edits: number; created: number }[];
  topPages: { title: string; author: string; version: number; isNew: boolean }[];
  indicators: { name: string; label: string; severity: string; baseline: string }[];
}

export function generateConfluencePDFReport(data: ConfluenceReportData): Uint8Array {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = 20;

  // ── Header ──
  doc.setFillColor(6, 182, 212); // cyan-500
  doc.rect(0, 0, 210, 14, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("VeriGit — Confluence Activity Report", 105, 9, { align: "center" });

  y = 22;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.black);
  doc.text(data.spaceName || data.spaceKey, 105, y, { align: "center" });
  y += 7;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.gray);
  if (data.cloudName) { doc.text(data.cloudName, 105, y, { align: "center" }); y += 4; }
  doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 105, y, { align: "center" });
  y += 4;
  doc.text(`Period: ${formatDate(data.dateRange.start)} – ${formatDate(data.dateRange.end)}`, 105, y, { align: "center" });
  y += 10;

  // ── Summary boxes ──
  const boxes = [
    { label: "Total Pages", value: String(data.totalPages) },
    { label: "Contributors", value: String(data.uniqueContributors) },
    { label: "Pages/Day", value: String(data.pagesPerDay) },
    { label: "New Content", value: `${data.newPagesRatio}%` },
  ];
  const boxW = 40, boxH = 16, gap = 5;
  const startX = (210 - (boxes.length * boxW + (boxes.length - 1) * gap)) / 2;
  boxes.forEach((b, i) => {
    const bx = startX + i * (boxW + gap);
    doc.setFillColor(240, 253, 254);
    doc.setDrawColor(6, 182, 212);
    doc.roundedRect(bx, y, boxW, boxH, 2, 2, "FD");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.blue);
    doc.text(b.value, bx + boxW / 2, y + 9, { align: "center" });
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.gray);
    doc.text(b.label, bx + boxW / 2, y + 14, { align: "center" });
  });
  y += boxH + 8;

  // ── Indicators table ──
  if (data.indicators.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Health Indicators", 20, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Indicator", "Value", "Baseline", "Status"]],
      body: data.indicators.map(ind => [
        ind.name,
        ind.label,
        ind.baseline,
        `${severitySymbol(ind.severity)} ${ind.severity.charAt(0).toUpperCase() + ind.severity.slice(1)}`,
      ]),
      theme: "grid",
      headStyles: { fillColor: [6, 182, 212], textColor: 255, fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 3: { fontStyle: "bold" } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell(hookData: any) {
        if (hookData.section === "body" && hookData.column.index === 3) {
          const raw = String(hookData.cell.raw);
          hookData.cell.styles.textColor = raw.startsWith("+") ? [5,150,105] : raw.startsWith("!") ? [217,119,6] : [220,38,38];
        }
      },
      margin: { left: 20, right: 20 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  y = checkPageBreak(doc, y);

  // ── Contributors table ──
  if (data.authors.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Top Contributors", 20, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Contributor", "Pages", "Share %"]],
      body: data.authors.slice(0, 20).map(a => [
        a.name,
        String(a.count),
        `${data.totalPages > 0 ? ((a.count / data.totalPages) * 100).toFixed(1) : 0}%`,
      ]),
      theme: "striped",
      headStyles: { fillColor: [6, 182, 212], textColor: 255, fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  y = checkPageBreak(doc, y);

  // ── Daily activity table ──
  if (data.dailyCounts.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Daily Activity", 20, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Date", "New Pages", "Edits", "Total"]],
      body: data.dailyCounts.slice(-30).map(d => [
        d.date,
        String(d.created),
        String(d.edits),
        String(d.created + d.edits),
      ]),
      theme: "striped",
      headStyles: { fillColor: [6, 182, 212], textColor: 255, fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  y = checkPageBreak(doc, y);

  // ── Top pages table ──
  if (data.topPages.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.black);
    doc.text("Recent Pages", 20, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Page Title", "Author", "Version", "Type"]],
      body: data.topPages.slice(0, 30).map(p => [
        p.title.length > 50 ? p.title.slice(0, 47) + "..." : p.title,
        p.author,
        String(p.version),
        p.isNew ? "New" : "Edit",
      ]),
      theme: "striped",
      headStyles: { fillColor: [6, 182, 212], textColor: 255, fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
    });
  }

  // ── Footer ──
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.lightGray);
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`Confluence Activity Report generated by VeriGit | Space: ${data.spaceKey} | Page ${i}/${pageCount}`, 105, 290, { align: "center" });
  }

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ==================== Download Helper ====================

export function downloadPDF(data: Uint8Array | ArrayBuffer, filename: string): void {
  const blob = new Blob([data as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
